/**
 * Every scorecard category: its id (as written in a check's
 * `metadata.category`), label and display order. `Other` covers anything
 * unrecognised.
 * @public
 */
export const SCORECARD_CATEGORIES = Object.freeze([
  { id: "Ownership", label: "Ownership", order: 0 },
  { id: "Security", label: "Security", order: 1 },
  { id: "Reliability", label: "Reliability", order: 2 },
  { id: "Documentation", label: "Documentation", order: 3 },
  { id: "Delivery", label: "Delivery", order: 4 },
  { id: "Code health", label: "Code health", order: 5 },
  { id: "Other", label: "Other", order: 6 },
] as const);

/** One row of SCORECARD_CATEGORIES. @public */
export type ScorecardCategoryDefinition = (typeof SCORECARD_CATEGORIES)[number];

/** A check's category for display. @public */
export type ScorecardCategory = ScorecardCategoryDefinition["id"];

/** The category ids in display order. @public */
export const SCORECARD_CATEGORY_IDS = SCORECARD_CATEGORIES.map(
  (c) => c.id
) as unknown as readonly [ScorecardCategory, ...ScorecardCategory[]];

const BY_ID = new Map<string, ScorecardCategoryDefinition>(
  SCORECARD_CATEGORIES.map((c) => [c.id, c])
);

const BY_LOWER_CASE = new Map<string, ScorecardCategory>(
  SCORECARD_CATEGORIES.map((c) => [c.id.toLocaleLowerCase("en-US"), c.id])
);

/** True for a category id exactly as SCORECARD_CATEGORIES spells it. @public */
export function isScorecardCategory(raw: unknown): raw is ScorecardCategory {
  return typeof raw === "string" && BY_ID.has(raw);
}

/** Matches a raw `metadata.category` case-insensitively. @public */
export function categoryOf(raw: unknown): ScorecardCategory {
  if (typeof raw !== "string") return "Other";
  return BY_LOWER_CASE.get(raw.toLocaleLowerCase("en-US")) ?? "Other";
}

/** Fixed category order, `Other` last. @public */
export function compareCategories(
  a: ScorecardCategory,
  b: ScorecardCategory
): number {
  return BY_ID.get(a)!.order - BY_ID.get(b)!.order;
}

/** By category in the fixed order, then by name: the Scorecards by-check view's order. @public */
export function compareChecks(
  a: { category: ScorecardCategory; name: string },
  b: { category: ScorecardCategory; name: string }
): number {
  return (
    compareCategories(a.category, b.category) || a.name.localeCompare(b.name)
  );
}
